import type { ReactNode } from "react";
import { AlertTriangle } from "lucide-react";

/**
 * HOTELGATE-1 · 多日游每个过夜的晚上必须二选一：选酒店 / 明确「不住」；都没选 = 未定（标红）。
 * 草稿里用一个字符串承载三态：酒店 id / NO_STAY（不住）/ ""（未定），提交时用 ``hotelPayload`` 拆成 default_hotel_id + no_overnight。
 * 只给非套团产品用（套团的酒店 / 不住在线路配置里选）。
 */

export const NO_STAY = "__none__";

/** 草稿值 → 提交字段：不住 → no_overnight=true、酒店清空；选了酒店 → 酒店 id、no_overnight=false；未定 → 都空。 */
export function hotelPayload(v: string | null | undefined): { default_hotel_id: string | null; no_overnight: boolean } {
  if (v === NO_STAY) return { default_hotel_id: null, no_overnight: true };
  return { default_hotel_id: v || null, no_overnight: false };
}

/** 读出来的 leg → 草稿值。 */
export function hotelDraftOf(leg: { default_hotel_id?: string | null; no_overnight?: boolean } | undefined | null): string {
  if (!leg) return "";
  return leg.no_overnight ? NO_STAY : leg.default_hotel_id ?? "";
}

interface HotelLite { id: string; name_cn: string; star_tier?: number | null; location_city?: string | null }

export function OvernightHotelSelect({ value, onChange, hotels, isLast = false, className, testId = "overnight-hotel" }: {
  value: string;
  onChange: (v: string) => void;
  hotels: HotelLite[];
  /** 行程最后一天不住，不用选（空着也不标红） */
  isLast?: boolean;
  className?: string;
  testId?: string;
}) {
  const undecided = !value && !isLast;
  const cls = className ?? "rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500";
  return (
    <div className="flex flex-col gap-1">
      <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId}
        className={`${cls} ${undecided ? "!border-rose-400 !bg-rose-50" : ""}`}>
        <option value="">{isLast ? "最后一天不住，不用选" : "请选择（未定）"}</option>
        <option value={NO_STAY}>不住（当日返程 / 无住宿）</option>
        {hotels.map((h) => (
          <option key={h.id} value={h.id}>{h.name_cn}{h.star_tier ? ` ${"★".repeat(h.star_tier)}` : ""}{h.location_city ? ` · ${h.location_city}` : ""}</option>
        ))}
      </select>
      {undecided && (
        <span className="inline-flex items-center gap-1 text-[11px] font-medium text-rose-600" data-testid={`${testId}-undecided`}>
          <AlertTriangle size={11} />未定：选酒店，或选「不住」
        </span>
      )}
    </div>
  );
}

/** 价表顶部红条：酒店没配齐时，下面的价格没算酒店，不能用来报价。 */
export interface OvernightMissingItem { night_no: number; line_day: number | null; city: string | null; reason: string }

export function OvernightMissingBar({ missing, children }: { missing?: OvernightMissingItem[] | null; children?: ReactNode }) {
  if (!missing || missing.length === 0) return null;
  const nights = missing.map((m) => m.night_no).join("、");
  return (
    <div role="alert" data-testid="overnight-missing-bar"
      className="mb-3 flex flex-wrap items-center gap-2 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-[12.5px] text-rose-700">
      <AlertTriangle size={14} className="shrink-0" />
      <span><b>酒店没配齐</b>（第 {nights} 晚），以下价格没算酒店，<b>不能用来报价</b>。</span>
      {children}
    </div>
  );
}
